import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Lock, LogOut, Image as ImageIcon, FileText, CalendarDays, Mail,
  Plus, Trash2, ArrowLeft, Stethoscope, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import {
  login as apiLogin, logout as apiLogout, verifyAdmin, errorMessage,
  listPhotos, uploadPhoto, deletePhoto,
  listBlogs, createBlog, deleteBlog,
  listEvents, createEvent, deleteEvent,
  listMessages, deleteMessage,
} from "../lib/api";

const BLOG_CATEGORIES = ["Neurology", "Philosophy", "Community", "Literature", "Education"];
const PHOTO_CATEGORIES = ["Recent", "Practice", "Author", "Service", "Honors", "Public Life", "Family", "Portrait"];

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ u: "", p: "" });
  const [loggingIn, setLoggingIn] = useState(false);

  const [photos, setPhotos] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);

  // Verify existing token on mount
  useEffect(() => {
    const token = sessionStorage.getItem("sp_token");
    if (!token) { setChecking(false); return; }
    verifyAdmin()
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false))
      .finally(() => setChecking(false));
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      const [p, b, e, m] = await Promise.all([
        listPhotos(), listBlogs(), listEvents(), listMessages(),
      ]);
      setPhotos(p); setBlogs(b); setEvents(e); setMessages(m);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load data"));
    }
  }, []);

  useEffect(() => { if (authed) refreshAll(); }, [authed, refreshAll]);

  const doLogin = async (e) => {
    e.preventDefault();
    if (!creds.u || !creds.p) return toast.error("Please enter credentials");
    setLoggingIn(true);
    try {
      await apiLogin(creds.u, creds.p);
      setAuthed(true);
      toast.success("Welcome back, Doctor.");
    } catch (err) {
      toast.error(errorMessage(err, "Invalid credentials"));
    } finally {
      setLoggingIn(false);
    }
  };

  const doLogout = () => { apiLogout(); setAuthed(false); toast.success("Logged out"); };

  // ---- Photos ----
  const [newPhoto, setNewPhoto] = useState({ caption: "", category: "Recent", file: null });
  const [uploading, setUploading] = useState(false);
  const addPhoto = async () => {
    if (!newPhoto.file) return toast.error("Choose an image");
    setUploading(true);
    try {
      const saved = await uploadPhoto(newPhoto);
      setPhotos([saved, ...photos]);
      setNewPhoto({ caption: "", category: "Recent", file: null });
      // Reset the file input
      const fileInput = document.getElementById("admin-photo-file");
      if (fileInput) fileInput.value = "";
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };
  const removePhoto = async (id) => {
    try {
      await deletePhoto(id);
      setPhotos(photos.filter((p) => p.id !== id));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    }
  };

  // ---- Blogs ----
  const [newBlog, setNewBlog] = useState({ title: "", category: "Neurology", excerpt: "", body: "", cover: "" });
  const [publishing, setPublishing] = useState(false);
  const addBlog = async () => {
    if (!newBlog.title || !newBlog.body) return toast.error("Title & body required");
    setPublishing(true);
    try {
      const saved = await createBlog(newBlog);
      setBlogs([saved, ...blogs]);
      setNewBlog({ title: "", category: "Neurology", excerpt: "", body: "", cover: "" });
      toast.success("Blog post published");
    } catch (err) {
      toast.error(errorMessage(err, "Publish failed"));
    } finally {
      setPublishing(false);
    }
  };
  const removeBlog = async (id) => {
    try {
      await deleteBlog(id);
      setBlogs(blogs.filter((b) => b.id !== id));
      toast.success("Blog removed");
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    }
  };

  // ---- Events ----
  const [newEvent, setNewEvent] = useState({ title: "", date: "", location: "", description: "", type: "upcoming" });
  const [saving, setSaving] = useState(false);
  const addEvent = async () => {
    if (!newEvent.title || !newEvent.date) return toast.error("Title & date required");
    setSaving(true);
    try {
      const saved = await createEvent(newEvent);
      setEvents([saved, ...events]);
      setNewEvent({ title: "", date: "", location: "", description: "", type: "upcoming" });
      toast.success("Event added");
    } catch (err) {
      toast.error(errorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };
  const removeEvent = async (id) => {
    try {
      await deleteEvent(id);
      setEvents(events.filter((e) => e.id !== id));
      toast.success("Event removed");
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    }
  };

  // ---- Messages ----
  const removeMessage = async (id) => {
    try {
      await deleteMessage(id);
      setMessages(messages.filter((m) => m.id !== id));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-emerald-deep flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" data-testid="admin-checking-spinner" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-emerald-deep flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 bg-ivory border-0 shadow-2xl" data-testid="admin-login-card">
          <div className="flex items-center gap-3 mb-7">
            <div className="w-12 h-12 rounded-full bg-emerald-deep flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-gold" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gold">Admin</div>
              <div className="font-display text-2xl text-emerald-deep">Sign in</div>
            </div>
          </div>
          <form onSubmit={doLogin} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-600">Username</label>
              <Input
                data-testid="admin-login-username"
                value={creds.u}
                onChange={(e) => setCreds({ ...creds, u: e.target.value })}
                className="mt-1.5 h-11"
                placeholder="admin"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-600">Password</label>
              <Input
                data-testid="admin-login-password"
                type="password"
                value={creds.p}
                onChange={(e) => setCreds({ ...creds, p: e.target.value })}
                className="mt-1.5 h-11"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <Button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loggingIn}
              className="w-full bg-emerald-deep hover:bg-[#1F2A3F] text-ivory h-11"
            >
              {loggingIn ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              {loggingIn ? "Signing in…" : "Login"}
            </Button>
            <button
              data-testid="admin-login-back"
              type="button"
              onClick={() => navigate("/")}
              className="w-full text-sm text-stone-600 hover:text-emerald-deep flex items-center justify-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Back to site
            </button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="bg-emerald-deep text-ivory">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-gold" />
            </div>
            <div>
              <div className="font-display text-xl">Admin Panel</div>
              <div className="text-xs tracking-widest uppercase text-gold">Dr. Shaji Prabhakaran</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-view-site-btn" variant="ghost" onClick={() => navigate("/")} className="text-ivory hover:bg-ivory/10">
              <ArrowLeft className="w-4 h-4 mr-1" /> View site
            </Button>
            <Button data-testid="admin-logout-btn" onClick={doLogout} className="bg-gold text-emerald-deep hover:bg-gold/90">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <Tabs defaultValue="photos">
          <TabsList className="bg-cream">
            <TabsTrigger data-testid="admin-tab-photos" value="photos" className="data-[state=active]:!bg-emerald-deep data-[state=active]:!text-ivory">
              <ImageIcon className="w-4 h-4 mr-1.5" /> Photos ({photos.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-blogs" value="blogs" className="data-[state=active]:!bg-emerald-deep data-[state=active]:!text-ivory">
              <FileText className="w-4 h-4 mr-1.5" /> Blogs ({blogs.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-events" value="events" className="data-[state=active]:!bg-emerald-deep data-[state=active]:!text-ivory">
              <CalendarDays className="w-4 h-4 mr-1.5" /> Events ({events.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-inbox" value="inbox" className="data-[state=active]:!bg-emerald-deep data-[state=active]:!text-ivory">
              <Mail className="w-4 h-4 mr-1.5" /> Inbox ({messages.length})
            </TabsTrigger>
          </TabsList>

          {/* Photos */}
          <TabsContent value="photos" className="mt-6">
            <Card className="p-6 bg-ivory border-stone-200/60">
              <div className="font-display text-xl text-emerald-deep mb-4">Upload a photo</div>
              <div className="grid md:grid-cols-4 gap-4">
                <Input
                  id="admin-photo-file"
                  data-testid="admin-photo-file"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewPhoto({ ...newPhoto, file: e.target.files?.[0] || null })}
                  className="h-11"
                />
                <Input
                  data-testid="admin-photo-caption"
                  value={newPhoto.caption}
                  onChange={(e) => setNewPhoto({ ...newPhoto, caption: e.target.value })}
                  placeholder="Caption"
                  className="h-11"
                />
                <Select value={newPhoto.category} onValueChange={(v) => setNewPhoto({ ...newPhoto, category: v })}>
                  <SelectTrigger data-testid="admin-photo-category" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PHOTO_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="admin-photo-upload-btn"
                  onClick={addPhoto}
                  disabled={uploading}
                  className="bg-emerald-deep hover:bg-[#1F2A3F] text-ivory h-11"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  {uploading ? "Uploading…" : "Add photo"}
                </Button>
              </div>
              <p className="text-xs text-stone-500 mt-3">Max 12 MB · JPG, PNG, WEBP, GIF, AVIF</p>
            </Card>
            <div className="mt-6 grid sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-photos-grid">
              {photos.length === 0 && <p className="text-stone-500">No custom photos uploaded yet.</p>}
              {photos.map((p) => (
                <div key={p.id} className="bg-ivory rounded-xl overflow-hidden border border-stone-200 group">
                  <img src={p.src} alt={p.caption} className="w-full aspect-square object-cover" />
                  <div className="p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-stone-700 truncate">{p.caption}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gold">{p.category}</div>
                    </div>
                    <button
                      data-testid={`admin-photo-delete-${p.id}`}
                      onClick={() => removePhoto(p.id)}
                      className="text-stone-400 hover:text-red-600"
                      aria-label="Delete photo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Blogs */}
          <TabsContent value="blogs" className="mt-6">
            <Card className="p-6 bg-ivory border-stone-200/60 space-y-3">
              <div className="font-display text-xl text-emerald-deep">Write a blog post</div>
              <div className="grid md:grid-cols-2 gap-3">
                <Input
                  data-testid="admin-blog-title"
                  value={newBlog.title}
                  onChange={(e) => setNewBlog({ ...newBlog, title: e.target.value })}
                  placeholder="Title"
                  className="h-11"
                />
                <Select value={newBlog.category} onValueChange={(v) => setNewBlog({ ...newBlog, category: v })}>
                  <SelectTrigger data-testid="admin-blog-category" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BLOG_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input
                data-testid="admin-blog-cover"
                value={newBlog.cover}
                onChange={(e) => setNewBlog({ ...newBlog, cover: e.target.value })}
                placeholder="Cover image URL (optional)"
                className="h-11"
              />
              <Textarea
                data-testid="admin-blog-excerpt"
                value={newBlog.excerpt}
                onChange={(e) => setNewBlog({ ...newBlog, excerpt: e.target.value })}
                placeholder="Short excerpt…"
                className="min-h-[70px]"
              />
              <Textarea
                data-testid="admin-blog-body"
                value={newBlog.body}
                onChange={(e) => setNewBlog({ ...newBlog, body: e.target.value })}
                placeholder="Full body of the post…"
                className="min-h-[160px]"
              />
              <Button
                data-testid="admin-blog-publish-btn"
                onClick={addBlog}
                disabled={publishing}
                className="bg-emerald-deep hover:bg-[#1F2A3F] text-ivory"
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                {publishing ? "Publishing…" : "Publish"}
              </Button>
            </Card>
            <div className="mt-6 space-y-3" data-testid="admin-blogs-list">
              {blogs.length === 0 && <p className="text-stone-500">No blog posts yet.</p>}
              {blogs.map((b) => (
                <div key={b.id} className="bg-ivory border border-stone-200 rounded-xl p-5 flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-gold">{b.category}</div>
                    <div className="font-display text-lg text-emerald-deep">{b.title}</div>
                    <p className="text-stone-600 text-sm mt-1">{b.excerpt}</p>
                  </div>
                  <button
                    data-testid={`admin-blog-delete-${b.id}`}
                    onClick={() => removeBlog(b.id)}
                    className="text-stone-400 hover:text-red-600"
                    aria-label="Delete blog"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Events */}
          <TabsContent value="events" className="mt-6">
            <Card className="p-6 bg-ivory border-stone-200/60 space-y-3">
              <div className="font-display text-xl text-emerald-deep">Add an event</div>
              <div className="grid md:grid-cols-2 gap-3">
                <Input
                  data-testid="admin-event-title"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  placeholder="Event title"
                  className="h-11"
                />
                <Input
                  data-testid="admin-event-date"
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="h-11"
                />
                <Input
                  data-testid="admin-event-location"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  placeholder="Location"
                  className="h-11"
                />
                <Select value={newEvent.type} onValueChange={(v) => setNewEvent({ ...newEvent, type: v })}>
                  <SelectTrigger data-testid="admin-event-type" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="past">Past</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                data-testid="admin-event-description"
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Description…"
                className="min-h-[100px]"
              />
              <Button
                data-testid="admin-event-save-btn"
                onClick={addEvent}
                disabled={saving}
                className="bg-emerald-deep hover:bg-[#1F2A3F] text-ivory"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                {saving ? "Saving…" : "Add event"}
              </Button>
            </Card>
            <div className="mt-6 space-y-3" data-testid="admin-events-list">
              {events.length === 0 && <p className="text-stone-500">No events yet.</p>}
              {events.map((ev) => (
                <div key={ev.id} className="bg-ivory border border-stone-200 rounded-xl p-5 flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-gold">{ev.type}</div>
                    <div className="font-display text-lg text-emerald-deep">{ev.title}</div>
                    <div className="text-stone-600 text-sm">{ev.date} · {ev.location}</div>
                  </div>
                  <button
                    data-testid={`admin-event-delete-${ev.id}`}
                    onClick={() => removeEvent(ev.id)}
                    className="text-stone-400 hover:text-red-600"
                    aria-label="Delete event"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Inbox */}
          <TabsContent value="inbox" className="mt-6">
            <div className="space-y-3" data-testid="admin-inbox-list">
              {messages.length === 0 && <p className="text-stone-500">No messages yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className="bg-ivory border border-stone-200 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg text-emerald-deep">{m.name}</div>
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-stone-500">{new Date(m.date).toLocaleString()}</div>
                      <button
                        data-testid={`admin-message-delete-${m.id}`}
                        onClick={() => removeMessage(m.id)}
                        className="text-stone-400 hover:text-red-600"
                        aria-label="Delete message"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-stone-600">
                    <a href={`mailto:${m.email}`} className="hover:text-emerald-deep">{m.email}</a>
                    {m.phone && <> · <a href={`tel:${m.phone}`} className="hover:text-emerald-deep">{m.phone}</a></>}
                  </div>
                  <p className="mt-3 text-stone-700 whitespace-pre-line">{m.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
